import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const ROICalculator = () => {
  const [inputs, setInputs] = useState({
    currentAISpend: '',
    employees: '',
    industry: '',
    serviceType: ''
  });
  const [results, setResults] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);

  const industryOptions = [
    { value: 'manufacturing', label: 'Manufacturing', description: 'Industrial and production companies' },
    { value: 'financial', label: 'Financial Services', description: 'Banks, insurance, fintech' },
    { value: 'healthcare', label: 'Healthcare', description: 'Hospitals, pharma, medical devices' },
    { value: 'retail', label: 'Retail & E-commerce', description: 'Consumer goods and services' },
    { value: 'technology', label: 'Technology', description: 'Software and tech companies' },
    { value: 'other', label: 'Other', description: 'Other industries' }
  ];

  const serviceOptions = [
    { value: 'strategic', label: 'Strategic Assessment', description: 'Strategy and planning focused' },
    { value: 'technical', label: 'Technical Implementation', description: 'Hands-on development' },
    { value: 'optimization', label: 'Ongoing Optimization', description: 'Performance improvement' }
  ];

  const calculateROI = () => {
    setIsCalculating(true);
    
    // Simulate calculation delay
    setTimeout(() => {
      const currentSpend = parseFloat(inputs?.currentAISpend) || 0;
      const employeeCount = parseInt(inputs?.employees) || 0;
      
      // Industry multipliers
      const industryMultipliers = {
        manufacturing: 1.4,
        financial: 1.6,
        healthcare: 1.3,
        retail: 1.5,
        technology: 1.7,
        other: 1.2
      };

      // Service type multipliers
      const serviceMultipliers = {
        strategic: 0.8,
        technical: 1.2,
        optimization: 1.5
      };

      const industryMultiplier = industryMultipliers?.[inputs?.industry] || 1.2;
      const serviceMultiplier = serviceMultipliers?.[inputs?.serviceType] || 1.0;

      // Calculate savings and benefits
      const vendorLockInSavings = currentSpend * 0.35 * industryMultiplier;
      const efficiencyGains = (employeeCount * 50000 * 0.25) * serviceMultiplier;
      const implementationCost = Math.max(150000, currentSpend * 0.4);
      
      const totalAnnualBenefits = vendorLockInSavings + efficiencyGains;
      const netBenefit = totalAnnualBenefits - implementationCost;
      const roiPercentage = ((netBenefit / implementationCost) * 100);
      const paybackMonths = Math.ceil((implementationCost / totalAnnualBenefits) * 12);

      setResults({
        vendorLockInSavings: Math.round(vendorLockInSavings),
        efficiencyGains: Math.round(efficiencyGains),
        totalAnnualBenefits: Math.round(totalAnnualBenefits),
        implementationCost: Math.round(implementationCost),
        netBenefit: Math.round(netBenefit),
        roiPercentage: Math.round(roiPercentage),
        paybackMonths,
        threeYearValue: Math.round(totalAnnualBenefits * 3 - implementationCost)
      });
      
      setIsCalculating(false);
    }, 1500);
  };

  const handleInputChange = (field, value) => {
    setInputs(prev => ({
      ...prev,
      [field]: value
    }));
    setResults(null);
  };

  const isFormValid = inputs?.currentAISpend && inputs?.employees && inputs?.industry && inputs?.serviceType;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })?.format(amount);
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="bg-gradient-to-r from-brand-primary to-brand-secondary p-6">
        <div className="flex items-center space-x-3 mb-2">
          <Icon name="Calculator" size={24} color="white" />
          <h3 className="text-xl font-bold text-white">
            ROI Calculator
          </h3>
        </div>
        <p className="text-white/90">
          Calculate your potential return on investment with AI Clean Core services
        </p>
      </div>
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <Input
            label="Current Annual AI Spending"
            type="number"
            placeholder="500000"
            value={inputs?.currentAISpend}
            onChange={(e) => handleInputChange('currentAISpend', e?.target?.value)}
            description="Include all AI-related costs (licenses, services, etc.)"
          />

          <Input
            label="Number of Employees"
            type="number"
            placeholder="250"
            value={inputs?.employees}
            onChange={(e) => handleInputChange('employees', e?.target?.value)}
            description="Total company headcount"
          />

          <Select
            label="Industry"
            options={industryOptions}
            value={inputs?.industry}
            onChange={(value) => handleInputChange('industry', value)}
            placeholder="Select your industry..."
          />

          <Select
            label="Service Type"
            options={serviceOptions}
            value={inputs?.serviceType}
            onChange={(value) => handleInputChange('serviceType', value)}
            placeholder="Select service type..."
          />
        </div>

        <Button
          variant="default"
          onClick={calculateROI}
          disabled={!isFormValid}
          loading={isCalculating}
          fullWidth
          className="bg-brand-primary hover:bg-brand-primary/90 mb-6"
          iconName="Calculator"
          iconPosition="left"
        >
          {isCalculating ? 'Calculating...' : 'Calculate ROI'}
        </Button>

        {results && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-success/10 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-success mb-1">
                  {results?.roiPercentage}%
                </div>
                <div className="text-sm text-text-secondary">First Year ROI</div>
              </div>
              
              <div className="bg-brand-primary/10 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-brand-primary mb-1">
                  {results?.paybackMonths}
                </div>
                <div className="text-sm text-text-secondary">Months to Payback</div>
              </div>
              
              <div className="bg-brand-secondary/10 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-brand-secondary mb-1">
                  {formatCurrency(results?.threeYearValue)}
                </div>
                <div className="text-sm text-text-secondary">3-Year Net Value</div>
              </div>
            </div>

            <div className="bg-brand-surface rounded-lg p-6">
              <h4 className="font-semibold text-text-primary mb-4">Detailed Breakdown</h4>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-text-secondary">Vendor Lock-in Savings (Annual)</span>
                  <span className="font-semibold text-success">
                    +{formatCurrency(results?.vendorLockInSavings)}
                  </span>
                </div>
                
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-text-secondary">Efficiency Gains (Annual)</span>
                  <span className="font-semibold text-success">
                    +{formatCurrency(results?.efficiencyGains)}
                  </span>
                </div>
                
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-text-secondary">Implementation Investment</span>
                  <span className="font-semibold text-text-primary">
                    -{formatCurrency(results?.implementationCost)}
                  </span>
                </div>
                
                <div className="flex justify-between items-center py-2 pt-4 border-t-2 border-brand-primary">
                  <span className="font-semibold text-text-primary">Net Annual Benefit</span>
                  <span className="font-bold text-brand-primary text-lg">
                    {formatCurrency(results?.netBenefit)}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start space-x-2">
                <Icon name="Info" size={20} color="var(--color-warning)" className="mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm text-yellow-800">
                    <strong>Note:</strong> These calculations are estimates based on industry averages and typical client outcomes. 
                    Actual results may vary based on your specific implementation and organizational factors.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ROICalculator;